import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, TextField, Button, IconButton, CircularProgress, Alert, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useLocation } from 'react-router-dom';
import AdminDashboardHeader from './components/AdminDashboardHeader';
import AdminDashboardSidebar from './components/AdminDashboardSidebar';
import AdminDashboardFooter from './components/AdminDashboardFooter';
import EmployeeListTable from './components/Employee/EmployeeListTable';
import EmployeeView from './components/Employee/EmployeeView';
import AddEmployee from './components/Employee/AddEmployee';
import EmployeeEdit from './components/Employee/EmployeeEdit';
import KPIMetricsSection from './components/KPIMetricsSection';
import MeetingStatisticsSection from './components/MeetingStatisticsSection';
import TablesSection from './components/TablesSection';
import DashboardChartsRow from './components/DashboardChartsRow';
import UpcomingMeetingsSection from './components/UpcomingMeetingsSection';
import UserListing from './components/User/UserListing';
import UserView from './components/User/UserView';
import AdminProfile from './pages/AdminProfile';
import AdminGmbManagement from '../../pages/AdminGmbManagement';
import ClientProfile from './components/Clients/ClientProfile';
import ClientListing from './components/Clients/ClientListing';
import PaymentProfile from './components/Clients/PaymentProfile';
import PaymentEditPage from './components/Clients/PaymentEditPage';
import EditClient from './components/Clients/EditClient';
import LeadListing from './components/Leads/LeadListing';
import MeetingProfile from './components/Meetings/MeetingProfile';
import MeetingListing from './components/Meetings/MeetingListing';

import ChangePassword from './pages/ChangePassword';
import HolidaysPage from './pages/Holidays';
import AddHolidayPage from './pages/AddHoliday';
import FestivalsPage from './pages/Festivals';
import AddFestivalPage from './pages/AddFestival';
import LocalCitationPage from './pages/LocalCitation';
import LeadProfile from './components/Leads/LeadProfile';
import AddLead from './components/Leads/AddLead';
import EditLead from './components/Leads/EditLead';
import { FollowupListing, AddFollowup, FollowupProfile } from './components/Followups';
import AddMeeting from './components/Meetings/AddMeeting';
import EditMeeting from './components/Meetings/EditMeeting';
import { WelcomeCallDashboard } from './components/WelcomeCall';
// dashboard now loads real data from backend; remove mock data dependency
import { getSession } from '../../utils/authUtils';
import ApiService from '../../services/api.js';
import { unwrapApi, extractArray } from '../../utils/apiHelpers';
import { useNavigate } from 'react-router-dom';
import AddClient from './pages/AddClient';
import Tasks from './pages/Tasks';
import Users from './pages/Users';
import Photos from './pages/Photos';
import PaymentsPage from './components/Payments/AdminPayments';
import ClientScores from './pages/ClientScores';
import CompetitorScore from './pages/CompetitorScore';
import EngagementDashboard from './components/Engagement/EngagementDashboard';
import { AdminRoleProvider } from '../../contexts/AdminRoleContext';

import logger from '../../utils/logger';
import TrialLinkGenerator from './components/TrialLinkGenerator';
import GmbCreationRequests from './pages/GmbCreationRequests';
// Mock employee data
const mockEmployees = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john.doe@example.com',
    department: 'Engineering',
    employee_code: 'EMP001',
    employee_type: 'Full-time',
    phone_number: '+1 123-456-7890',
    position: 'Software Engineer',
    joinDate: '2022-01-15',
    status: 'Active'
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    department: 'Product',
    employee_code: 'EMP002',
    employee_type: 'Full-time',
    phone_number: '+1 234-567-8901',
    position: 'Product Manager',
    joinDate: '2021-11-05',
    status: 'Active'
  },
  {
    id: 3,
    name: 'Robert Johnson',
    email: 'robert.johnson@example.com',
    department: 'Design',
    employee_code: 'EMP003',
    employee_type: 'Contract',
    phone_number: '+1 345-678-9012',
    position: 'UX Designer',
    joinDate: '2022-03-20',
    status: 'On Leave'
  },
  {
    id: 4,
    name: 'Emily Davis',
    email: 'emily.davis@example.com',
    department: 'Marketing',
    employee_code: 'EMP004',
    employee_type: 'Full-time',
    phone_number: '+1 456-789-0123',
    position: 'Marketing Specialist',
    joinDate: '2022-02-10',
    status: 'Active'
  },
  {
    id: 5,
    name: 'Michael Wilson',
    email: 'michael.wilson@example.com',
    department: 'Sales',
    employee_code: 'EMP005',
    employee_type: 'Part-time',
    phone_number: '+1 567-890-1234',
    position: 'Sales Representative',
    joinDate: '2021-10-25',
    status: 'Inactive'
  },
];

// Mock dashboard data used as a safe fallback when analytics/backend return empty
const MOCK_DASHBOARD = {
  breakdown: [
    { category: 'Local', count: 45, percentage: 38, color: '#34a853' },
    { category: 'Organic', count: 30, percentage: 25, color: '#0b91d6' },
    { category: 'Paid', count: 20, percentage: 17, color: '#f09b46' },
    { category: 'Referral', count: 20, percentage: 17, color: '#7b95ff' }
  ],
  counts: { basic: 40, advance: 50, pro: 20, none: 10 },
  total: 120,
  kpiMetrics: {
    totalRevenue: 0,
    oneTimeRevenue: 0,
    totalMRR: 0,
    oneTimeMRR: 0,
    totalClientMRR: 0,
    totalClientOneShort: 0,
    familyAccountCount: 0,
    totalAccounts: 0,
    profileActivityRatio: 0
  },
  clientSalesData: [
    { source: 'SEO', value: 45, color: '#34a853' },
    { source: 'GMB', value: 30, color: '#0b91d6' },
    { source: 'Ads', value: 20, color: '#f09b46' }
  ],
  taskManager: [
    { status: 'Pending', percentage: 28, color: '#7b95ff' },
    { status: 'Completed', percentage: 72, color: 'rgba(123, 149, 255, 0.40)' }
  ],
  upcomingMeetings: [
    { id: 'mck-1', client: 'Acme Corp', date_time: '2025-12-09T10:30:00Z', status: 'Scheduled', user: { name: 'John Doe' } },
    { id: 'mck-2', client: 'Green Gardens', date_time: '2025-12-10T14:00:00Z', status: 'Confirmed', user: { name: 'Jane Smith' } },
    { id: 'mck-3', client: 'Blue Bakery', date_time: '2025-12-11T09:00:00Z', status: 'Completed', user: { name: 'Sahil Patel' } }
  ],
  meetingDetails: [
    { status: 'Total Meeting', count: 3, color: '#0b91d6' },
    { status: 'Confirmed', count: 1, color: '#34a853' },
    { status: 'Scheduled', count: 1, color: '#fbbc05' }
  ]
};

const DashboardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f8f8',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column'
}));

const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '32px 40px',
  backgroundColor: '#f8f8f8'
}));

const DashboardTitleSection = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '32px',
  gap: '423px'
}));

const TitleContainer = styled(Stack)(({ theme }) => ({
  gap: '6px'
}));

const DashboardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 600,
  color: '#0b91d6'
}));

const DashboardSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#30302e'
}));

const SearchFilterContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '8px'
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '8px',
  border: '0.2px solid #a0a0aa'
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: '40px',
    paddingLeft: '32px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    '& fieldset': {
      border: 'none'
    },
    '& input': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      fontWeight: 400,
      color: '#a0a0aa',
      padding: '8px 12px 8px 0'
    }
  }
}));

const SearchIconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1
}));

const FilterButton = styled(Button)(({ theme }) => ({
  height: '40px',
  borderRadius: '8px',
  border: '0.2px solid #a0a0aa',
  backgroundColor: '#ffffff',
  color: '#0b91d6',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 16px',
  gap: '10px',
  '&:hover': {
    backgroundColor: '#f5f5f5'
  }
}));

const AdminDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(MOCK_DASHBOARD);
  const [dashboardError, setDashboardError] = useState(null);
  const [openTrialDialog, setOpenTrialDialog] = useState(false);
  const { id } = useParams();
  const location = useLocation();

  const navigate = useNavigate();

  useEffect(() => {
    // 1. Determine the Intended View based on URL
    let intendedView = 'dashboard';
    const path = location.pathname;

    if (path.includes('/employee-view/') || path.includes('/EmployeeView/')) intendedView = 'employeeView';
    else if (path.includes('/user-view/')) intendedView = 'userView';
    else if (path.includes('/employee-edit/')) intendedView = 'employeeEdit';
    else if (path.includes('/add-employee')) intendedView = 'addEmployee';
    else if (path.includes('/admin-dashboard/client/') || path.includes('/client/')) intendedView = 'clientProfile'; // Matches specific client profile
    else if (path.includes('/holidays')) intendedView = 'holidays';
    else if (path.includes('/add-holiday')) intendedView = 'addHoliday';
    else if (path.includes('/festivals')) intendedView = 'festivals';
    else if (path.includes('/add-festival') || path.includes('/edit-festival')) intendedView = 'addFestival';
    else if (path.includes('/local-citation')) intendedView = 'localCitation';
    else if (path.includes('/admin-dashboard/payments')) intendedView = 'payments';
    else if (path.includes('/admin/gmbs') || path.includes('/admin-dashboard/gmbs')) intendedView = 'gmbs';
    else if (path.includes('/lead/')) intendedView = 'leadProfile';
    else if (path.includes('/add-lead')) intendedView = 'addLead';
    else if (path.includes('/edit-lead/')) intendedView = 'editLead';
    else if (path.includes('/admin-dashboard/leads')) intendedView = 'leads';
    else if (path.includes('/admin-dashboard/meetings')) intendedView = 'meetings';
    else if (path.includes('/admin-dashboard/clients')) intendedView = 'clients';
    else if (path.includes('/admin-dashboard/employee')) intendedView = 'employee';
    else if (path.includes('/meeting/')) intendedView = 'meetingProfile';
    else if (path.includes('/edit-client/')) intendedView = 'editClient';
    else if (path.includes('/payment-profile/')) intendedView = 'paymentProfile';
    else if (path.includes('/payment-edit/')) intendedView = 'paymentEdit';
    else if (path.includes('/profile')) intendedView = 'profile';
    else if (path.includes('/change-password')) intendedView = 'changePassword';
    else if (path.includes('/gmb-manager')) intendedView = 'gmbs';
    else if (path.includes('/gmb-requests')) intendedView = 'gmbRequests';
    else if (path.includes('/tasks')) intendedView = 'tasks';
    else if (path.includes('/users')) intendedView = 'users'; // Users list
    else if (path.includes('/photos')) intendedView = 'photos';
    else if (path.includes('/client-scores')) intendedView = 'clientScores';
    else if (path.includes('/engagement')) intendedView = 'engagement';
    else if (path.includes('/add-client')) intendedView = 'addClient';
    else if (path.includes('/followup/')) intendedView = 'followupProfile';
    else if (path.includes('/add-followup')) intendedView = 'addFollowup';
    else if (path.includes('/followups')) intendedView = 'followups';
    else if (path.includes('/meeting/edit/')) intendedView = 'editMeeting';
    else if (path.includes('/add-meeting')) intendedView = 'addMeeting';
    else if (path.includes('/welcome-calls')) intendedView = 'welcomeCalls';
    else if (path === '/admin-dashboard') intendedView = 'dashboard';

    // 2. Role-Based Access Control (RBAC) - Only role_id 9 can access admin dashboard
    try {
      const userDataRaw = localStorage.getItem('userData');
      if (userDataRaw) {
        const userData = JSON.parse(userDataRaw);
        const roleId = Number(userData?.role_id || userData?.employee?.role_id);

        // Security Check: Only role_id 9 (Admin) can access admin dashboard
        if (roleId !== 9) {
          logger.warn(`[Security] Access denied to admin dashboard for Role ${roleId}. Redirecting to customer dashboard.`);
          navigate('/dashboard', { replace: true });
          return;
        }
      } else {
        // No user data - redirect to login
        logger.warn('[Security] No user data found. Redirecting to login.');
        navigate('/', { replace: true });
        return;
      }
    } catch (e) {
      logger.error('[AdminDashboard] Security check failed:', e);
      navigate('/dashboard', { replace: true });
      return;
    }

    // 3. Set the View
    setActiveView(intendedView);
    logger.log('[AdminDashboard] activeView set to:', intendedView);

  }, [location.pathname, navigate]);

  useEffect(() => {
    // Load dashboard data when dashboard view becomes active
    if (activeView === 'dashboard') {
      (async () => {
        // Ensure meetings are available as soon as possible (use meetings API directly)
        try {
          const meetRespEarly = await ApiService.get('/admin/meeting/get-meeting');
          const meetRootEarly = unwrapApi(meetRespEarly);
          const earlyMeetings = extractArray(meetRootEarly, 'meetings', 'rows');
          // Only set if we actually received meetings (avoid overriding with empty array)
          if (Array.isArray(earlyMeetings) && earlyMeetings.length > 0) {
            logger.log('[AdminDashboard] Early meetings fetched:', earlyMeetings.length);
            setDashboardData(prev => ({ ...(prev || {}), upcomingMeetings: earlyMeetings }));
          }
        } catch (earlyMeetErr) {
          logger.warn('[AdminDashboard] Early meetings fetch failed:', earlyMeetErr?.message || earlyMeetErr);
        }


        // Part 1: Client Breakdown (Primary then Fallback)
        try {
          const resp = await ApiService.get('/admin/analytics/client-category-breakdown');
          const payload = unwrapApi(resp);
          if (payload && (payload.breakdown || payload.counts)) {
            setDashboardData(prev => ({ ...(prev || {}), ...payload }));
            setDashboardError(null);
          } else {
            // Internal Fallback within breakdown logic
            const gmbResp = await ApiService.get('/gmb/admin/all');
            const root = gmbResp?.data ?? gmbResp;
            const rows = Array.isArray(root) ? root : (Array.isArray(root?.data) ? root.data : []);
            if (Array.isArray(rows) && rows.length > 0) {
              const counts = { basic: 0, advance: 0, pro: 0, none: 0 };
              rows.forEach(r => {
                const plan = (r.planType || r.planTier || r.plan_type || '').toString().toLowerCase();
                if (plan.includes('pro')) counts.pro += 1;
                else if (plan.includes('advance') || plan.includes('advanced')) counts.advance += 1;
                else if (plan.includes('basic')) counts.basic += 1;
                else counts.none += 1;
              });
              const total = counts.basic + counts.advance + counts.pro + counts.none;
              const breakdown = [];
              if (total > 0) {
                const pushIf = (label, cnt, color) => breakdown.push({ category: label, count: cnt, percentage: Math.round((cnt / total) * 100), color });
                pushIf('Basic', counts.basic, '#7CB9E8');
                pushIf('Advance', counts.advance, '#4CAF50');
                pushIf('Pro', counts.pro, '#FFB74D');
                pushIf('No Plan', counts.none, '#90A4AE');
              }
              setDashboardData(prev => ({ ...(prev || {}), breakdown, counts, total }));
            }
          }
        } catch (err) {
          logger.warn('[AdminDashboard] Breakdown fetch failed:', err?.message || err);
          setDashboardError(err?.message || String(err));
        }

        // Part 2: Engagement and Revenue (Always run)
        try {
          const engResp = await ApiService.get('/admin/analytics/engagement');
          const engRoot = unwrapApi(engResp);
          const engData = engRoot || {};
          if (engData) {
            const engagementMetrics = engData.engagementMetrics || engData;
            const active = engagementMetrics?.activeUsers || {};
            const daily = Number(active.daily) || 0;
            const monthly = Number(active.monthly) || 0;
            const profileActivityRatio = monthly > 0 ? Math.round((daily / monthly) * 100) : 0;

            const clientSalesData = Array.isArray(engData.featureUsageData)
              ? engData.featureUsageData.map((f, idx) => ({ source: f.name, value: f.value || 0, color: ['#34a853', '#0b91d6', '#f09b46', '#7b95ff'][idx % 4] }))
              : [];

            const post = engData.postCreation || {};
            const totalPosts = post.total || 0;
            const completedPosts = post.completed || 0;
            const pendingPosts = post.pending || Math.max(0, totalPosts - completedPosts);
            const taskManager = [
              { status: 'Pending', percentage: totalPosts > 0 ? Math.round((pendingPosts / totalPosts) * 100) : 0, color: '#7b95ff' },
              { status: 'Completed', percentage: totalPosts > 0 ? Math.round((completedPosts / totalPosts) * 100) : 0, color: 'rgba(123, 149, 255, 0.40)' }
            ];

            const familyFeature = (engData.featureUsageData && engData.featureUsageData.find(f => /family/i.test(f.name))) || null;
            const familyAccountCount = (familyFeature && familyFeature.value) || 0;

            setDashboardData(prev => ({
              ...(prev || {}),
              engagementMetrics: engagementMetrics,
              kpiMetrics: {
                totalRevenue: engData.totalRevenue || engagementMetrics.totalRevenue || 0,
                oneTimeRevenue: engData.oneTimeRevenue || engagementMetrics.oneTimeRevenue || 0,
                oneTimeMRR: engData.oneTimeRevenue || engagementMetrics.oneTimeRevenue || 0,
                totalClientMRR: post.total || 0,
                totalClientOneShort: post.pending || 0,
                familyAccountCount,
                totalAccounts: prev?.total || engData.totalAccounts || 0,
                profileActivityRatio
              },
              clientSalesData,
              taskManager
            }));
          }
        } catch (engErr) {
          logger.warn('[AdminDashboard] Engagement analytics fetch failed:', engErr?.message || engErr);
          setDashboardError(prev => prev ? `${prev}; Engagement error: ${engErr?.message || engErr}` : (engErr?.message || String(engErr)));
        }

        try {
          // Fetch upcoming meetings list for the upcoming meetings widget
          const meetResp = await ApiService.get('/admin/meeting/get-meeting');
          const meetRoot = unwrapApi(meetResp);
          // Normalize meetings array
          const meetings = extractArray(meetRoot, 'meetings', 'rows');
          logger.log('[AdminDashboard] Meetings fetched:', Array.isArray(meetings) ? meetings.length : 'no-array', meetings && meetings.slice ? meetings.slice(0, 3) : meetings);
          if (Array.isArray(meetings) && meetings.length > 0) {
            // Build meeting statistics
            const countsByStatus = meetings.reduce((acc, m) => {
              const s = (m.status || m.meeting_status || 'Unknown').toString();
              acc[s] = (acc[s] || 0) + 1;
              return acc;
            }, {});
            const meetingDetails = [
              { status: 'Total Meeting', count: meetings.length, color: '#0b91d6' },
              { status: 'Confirmed', count: countsByStatus['Confirmed'] || countsByStatus['confirmed'] || countsByStatus['CONFIRMED'] || 0, color: '#34a853' },
              { status: 'Scheduled', count: countsByStatus['Scheduled'] || countsByStatus['scheduled'] || 0, color: '#fbbc05' },
            ];
            setDashboardData(prev => ({ ...(prev || {}), upcomingMeetings: meetings, meetingDetails }));
          } else {
            // Ensure we clear any previous meetingDetails if none returned
            setDashboardData(prev => ({ ...(prev || {}), upcomingMeetings: meetings || [], meetingDetails: prev?.meetingDetails || [] }));
          }
        } catch (meetErr) {
          logger.warn('[AdminDashboard] Meetings fetch failed:', meetErr?.message || meetErr);
          setDashboardError(prev => prev ? `${prev}; Meetings error: ${meetErr?.message || meetErr}` : (meetErr?.message || String(meetErr)));
        }

        // Fetch total GMB profiles and plan breakdown (basic/advance/pro/none)
        try {
          const gmbAllResp = await ApiService.get('/gmb/admin/all');
          const gmbRoot = unwrapApi(gmbAllResp) || gmbAllResp;
          const gmbRows = Array.isArray(gmbRoot) ? gmbRoot : (Array.isArray(gmbRoot?.data) ? gmbRoot.data : []);
          const totalGmb = gmbRows.length || 0;

          const counts = { basic: 0, advance: 0, pro: 0, none: 0 };
          gmbRows.forEach(r => {
            const plan = (r.planType || r.planTier || r.plan_type || r.plan || '').toString().toLowerCase();
            if (plan.includes('pro')) counts.pro += 1;
            else if (plan.includes('advance') || plan.includes('advanced')) counts.advance += 1;
            else if (plan.includes('basic')) counts.basic += 1;
            else counts.none += 1;
          });

          setDashboardData(prev => ({ ...(prev || {}), kpiMetrics: { ...(prev?.kpiMetrics || {}), totalAccounts: totalGmb }, total: prev?.total || totalGmb, counts, gmbSample: gmbRows.slice(0, 8) }));

          logger.log('[AdminDashboard] GMB counts set on dashboardData:', counts);
        } catch (gmbCountErr) {
          logger.warn('[AdminDashboard] Failed to fetch total GMB count and plan breakdown:', gmbCountErr?.message || gmbCountErr);
        }

        // Last resort: if we still have no meaningful dashboard data, populate with MOCK_DASHBOARD
        setDashboardData(prev => {
          const hasBreakdown = Array.isArray(prev?.breakdown) && prev.breakdown.length > 0;
          const hasCounts = prev?.counts && Object.keys(prev.counts).length > 0;
          const hasMetrics = prev?.kpiMetrics && (prev.kpiMetrics.totalRevenue > 0 || prev.kpiMetrics.totalAccounts > 0);

          const needMock = !hasBreakdown && !hasCounts && !hasMetrics;

          if (needMock) {
            logger.log('[AdminDashboard] Using MOCK_DASHBOARD fallback');
            return { ...(prev || {}), ...MOCK_DASHBOARD, comingSoon: true };
          }

          // If we have some data, just fill in the gaps without overwriting real work
          return {
            ...MOCK_DASHBOARD,
            ...(prev || {}),
            kpiMetrics: {
              ...(MOCK_DASHBOARD.kpiMetrics || {}),
              ...(prev?.kpiMetrics || {})
            },
            breakdown: prev?.breakdown || [],
            counts: prev?.counts || {},
            total: prev?.total || 0,
            comingSoon: true
          };
        });
      })();
    }
  }, [activeView]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterClick = () => {
    logger.log('Filter clicked');
  };

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  return (
    <AdminRoleProvider>
      <DashboardContainer>
        <AdminDashboardHeader />
        <MainContent>
          <AdminDashboardSidebar onViewChange={handleViewChange} />
          <ContentArea>
            <DashboardTitleSection>
              <TitleContainer>
                {/* Title has been removed as per user action */}
              </TitleContainer>
              <></>
            </DashboardTitleSection>

            {activeView === 'dashboard' && (
              <>
                {dashboardError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {`Dashboard data could not be loaded from analytics: ${dashboardError}. Showing available data or placeholders.`}
                  </Alert>
                )}
                <KPIMetricsSection metrics={dashboardData?.kpiMetrics || {}} />
                <DashboardChartsRow
                  clientCategoryBreakdown={dashboardData?.breakdown || []}
                  planBreakdown={dashboardData?.counts || dashboardData?.breakdown || []}
                  totalClients={dashboardData?.total || dashboardData?.kpiMetrics?.totalAccounts || 0}
                  companyClientProfileStrength={dashboardData?.companyClientProfileStrength || {}}
                  clientSalesData={dashboardData?.clientSalesData || []}
                  taskManager={dashboardData?.taskManager || []}
                  packAppearance={dashboardData?.packAppearance || []}
                  profileStatus={dashboardData?.profileStatus || []}
                  support={dashboardData?.support || []}
                />
                <UpcomingMeetingsSection
                  upcomingMeetings={dashboardData?.upcomingMeetings || []}
                  targetVsAchieved={dashboardData?.targetVsAchieved || []}
                  meetingDetails={dashboardData?.meetingDetails || []}
                />
                <MeetingStatisticsSection meetingDetails={dashboardData?.meetingDetails || []} />
              </>
            )}

            {activeView === 'employee' && (
              <EmployeeListTable />
            )}

            {activeView === 'employeeView' && id && (
              <EmployeeView employeeId={id} />
            )}
            {activeView === 'employeeEdit' && id && (
              <EmployeeEdit />
            )}

            {activeView === 'addEmployee' && (
              <AddEmployee />
            )}

            {activeView === 'user' && (
              <React.Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
              </Box>}>
                <UserListing />
              </React.Suspense>
            )}

            {activeView === 'userView' && (
              <UserView />
            )}

            {activeView === 'profile' && (
              <AdminProfile />
            )}

            {activeView === 'changePassword' && (
              <ChangePassword />
            )}


            {(activeView === 'client' || activeView === 'clients') && (
              <React.Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
              </Box>}>
                <ClientListing />
              </React.Suspense>
            )}
            {activeView === 'clientProfile' && <ClientProfile />}
            {activeView === 'holidays' && <HolidaysPage />}
            {activeView === 'addHoliday' && <AddHolidayPage />}
            {activeView === 'festivals' && <FestivalsPage />}
            {activeView === 'addFestival' && <AddFestivalPage />}
            {activeView === 'localCitation' && <LocalCitationPage />}
            {activeView === 'leadProfile' && <LeadProfile />}
            {activeView === 'addLead' && <AddLead />}
            {activeView === 'editLead' && <EditLead />}
            {activeView === 'meetingProfile' && <MeetingProfile />}
            {activeView === 'paymentProfile' && <PaymentProfile />}
            {activeView === 'paymentEdit' && <PaymentEditPage />}
            {activeView === 'payments' && <PaymentsPage />}
            {activeView === 'gmbs' && <AdminGmbManagement />}
            {activeView === 'engagement' && <EngagementDashboard />}
            {activeView === 'gmbRequests' && <GmbCreationRequests />}

            {activeView === 'addClient' && <AddClient />}
            {activeView === 'editClient' && <EditClient />}
            {/* permissions view removed; route now shows GMB Manager */}
            {activeView === 'tasks' && <Tasks />}
            {activeView === 'users' && <Users />}
            {activeView === 'photos' && <Photos />}
            {activeView === 'clientScores' && <ClientScores />}

            {activeView === 'followups' && <FollowupListing />}
            {activeView === 'addFollowup' && <AddFollowup />}
            {activeView === 'followupProfile' && <FollowupProfile />}
            {activeView === 'addMeeting' && <AddMeeting />}
            {activeView === 'editMeeting' && <EditMeeting />}
            {activeView === 'welcomeCalls' && <WelcomeCallDashboard />}


            {activeView === 'leads' && (
              <React.Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
              </Box>}>
                <LeadListing />
              </React.Suspense>
            )}

            {(activeView === 'meetings' || activeView === 'meeting') && (
              <React.Suspense fallback={<Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
              </Box>}>
                <MeetingListing />
              </React.Suspense>
            )}
          </ContentArea>
        </MainContent>

        <AdminDashboardFooter />
      </DashboardContainer>
    </AdminRoleProvider>
  );
};

export default AdminDashboard;
